import { Grid, Slider, Typography, useMediaQuery } from '@mui/material';
import proptypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetFeatureReviewsQuery } from '../features/api/rest/apiFeatureReviewsSlice';

function ProductShowFeatures({ ean }) {
  const { t } = useTranslation();
  const matches = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [valueForMoney, setValueForMoney] = useState(0);
  const [healthiness, setHealthiness] = useState(0);
  const [taste, setTaste] = useState(0);
  const [texture, setTexture] = useState(0);
  const [packaging, setPackaging] = useState(0);
  const [durability, setDurability] = useState(0);
  const [availability, setAvailability] = useState(0);

  const { features } = useGetFeatureReviewsQuery(ean, {
    selectFromResult: ({ data }) => ({
      features: data,
    }),
    refetchOnMountOrArgChange: true,
  });

  const selectColor = (value) => {
    if (value <= 4.9) return 'red';
    if (value <= 6.9) return 'orange';
    return 'green';
  };

  useEffect(() => {
    setValueForMoney(parseFloat(features?.value_for_money || 0).toFixed(1));
    setHealthiness(parseFloat(features?.healthiness || 0).toFixed(1));
    setTaste(parseFloat(features?.taste || 0).toFixed(1));
    setTexture(parseFloat(features?.texture || 0).toFixed(1));
    setPackaging(parseFloat(features?.packaging || 0).toFixed(1));
    setDurability(parseFloat(features?.durability || 0).toFixed(1));
    setAvailability(parseFloat(features?.availability || 0).toFixed(1));
  }, [features]);

  if (matches) {
    return (
      <Grid container spacing={2} sx={{ pt: 3 }}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('reviews.features.title')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {t('reviews.features.valueForMoney')}
          </Typography>
        </Grid>
        <Grid item xs={10} sx={{ '& .MuiGrid-item': { pt: 0 }, pt: 0 }}>
          <Slider
            sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(valueForMoney) } }}
            aria-label="valueForMoney"
            aria-valuetext={valueForMoney.toString()}
            value={parseInt(valueForMoney, 10)}
            min={0}
            max={10}
            disabled
          />
        </Grid>
        <Grid item xs={2} sx={{ pt: 0 }}>
          {valueForMoney}
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {t('reviews.features.healthiness')}
          </Typography>
        </Grid>
        <Grid item xs={10} sx={{ pt: 0 }}>
          <Slider
            sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(healthiness) } }}
            aria-label="healthiness"
            aria-valuetext={healthiness.toString()}
            value={parseInt(healthiness, 10)}
            min={0}
            max={10}
            disabled
          />
        </Grid>
        <Grid item xs={2} sx={{ pt: 0 }}>
          {healthiness}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {t('reviews.features.taste')}
          </Typography>
        </Grid>
        <Grid item xs={10} sx={{ pt: 0 }}>
          <Slider sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(taste) } }} aria-label="taste" aria-valuetext={taste.toString()} value={parseInt(taste, 10)} min={0} max={10} disabled />
        </Grid>
        <Grid item xs={2} sx={{ pt: 0 }}>
          {taste}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {t('reviews.features.texture')}
          </Typography>
        </Grid>
        <Grid item xs={10} sx={{ pt: 0 }}>
          <Slider sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(texture) } }} aria-label="texture" aria-valuetext={texture.toString()} value={parseInt(texture, 10)} min={0} max={10} disabled />
        </Grid>
        <Grid item xs={2} sx={{ pt: 0 }}>
          {texture}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {t('reviews.features.packaging')}
          </Typography>
        </Grid>
        <Grid item xs={10} sx={{ pt: 0 }}>
          <Slider
            sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(packaging) } }}
            aria-label="packaging"
            aria-valuetext={packaging.toString()}
            value={parseInt(packaging, 10)}
            min={0}
            max={10}
            disabled
          />
        </Grid>
        <Grid item xs={2} sx={{ pt: 0 }}>
          {packaging}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {t('reviews.features.durability')}
          </Typography>
        </Grid>
        <Grid item xs={10} sx={{ pt: 0 }}>
          <Slider
            sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(durability) } }}
            aria-label="durability"
            aria-valuetext={durability.toString()}
            value={parseInt(durability, 10)}
            min={0}
            max={10}
            disabled
          />
        </Grid>
        <Grid item xs={2} sx={{ pt: 0 }}>
          {durability}
        </Grid>
        <Grid item xs={12} sm={12}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {t('reviews.features.availability')}
          </Typography>
        </Grid>
        <Grid item xs={10}>
          <Slider
            sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(availability) } }}
            aria-label="availability"
            aria-valuetext={availability.toString()}
            value={parseInt(availability, 10)}
            min={0}
            max={10}
            disabled
          />
        </Grid>
        <Grid item xs={2}>
          {availability}
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} sx={{ pt: 3 }}>
      <Grid item sm={12}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {t('reviews.features.title')}
        </Typography>
      </Grid>
      <Grid item sm={6}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {t('reviews.features.valueForMoney')}
        </Typography>
      </Grid>
      <Grid item sm={6}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {t('reviews.features.healthiness')}
        </Typography>
      </Grid>
      <Grid item sm={5} sx={{ '& .MuiGrid-item': { pt: 0 }, pt: 0 }}>
        <Slider
          sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(valueForMoney) } }}
          aria-label="valueForMoney"
          aria-valuetext={valueForMoney.toString()}
          value={parseInt(valueForMoney, 10)}
          min={0}
          max={10}
          disabled
        />
      </Grid>
      <Grid item sm={1} sx={{ pt: 0 }}>
        {valueForMoney}
      </Grid>
      <Grid item sm={5} sx={{ pt: 0 }}>
        <Slider
          sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(healthiness) } }}
          aria-label="healthiness"
          aria-valuetext={healthiness.toString()}
          value={parseInt(healthiness, 10)}
          min={0}
          max={10}
          disabled
        />
      </Grid>
      <Grid item sm={1} sx={{ pt: 0 }}>
        {healthiness}
      </Grid>
      <Grid item sm={6}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {t('reviews.features.taste')}
        </Typography>
      </Grid>
      <Grid item sm={6}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {t('reviews.features.texture')}
        </Typography>
      </Grid>
      <Grid item sm={5} sx={{ pt: 0 }}>
        <Slider sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(taste) } }} aria-label="taste" aria-valuetext={taste.toString()} value={parseInt(taste, 10)} min={0} max={10} disabled />
      </Grid>
      <Grid item sm={1} sx={{ pt: 0 }}>
        {taste}
      </Grid>
      <Grid item sm={5} sx={{ pt: 0 }}>
        <Slider sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(texture) } }} aria-label="texture" aria-valuetext={texture.toString()} value={parseInt(texture, 10)} min={0} max={10} disabled />
      </Grid>
      <Grid item sm={1} sx={{ pt: 0 }}>
        {texture}
      </Grid>
      <Grid item sm={6}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {t('reviews.features.packaging')}
        </Typography>
      </Grid>
      <Grid item sm={6}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {t('reviews.features.durability')}
        </Typography>
      </Grid>
      <Grid item sm={5} sx={{ pt: 0 }}>
        <Slider
          sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(packaging) } }}
          aria-label="packaging"
          aria-valuetext={packaging.toString()}
          value={parseInt(packaging, 10)}
          min={0}
          max={10}
          disabled
        />
      </Grid>
      <Grid item sm={1} sx={{ pt: 0 }}>
        {packaging}
      </Grid>
      <Grid item sm={5} sx={{ pt: 0 }}>
        <Slider
          sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(durability) } }}
          aria-label="durability"
          aria-valuetext={durability.toString()}
          value={parseInt(durability, 10)}
          min={0}
          max={10}
          disabled
        />
      </Grid>
      <Grid item sm={1} sx={{ pt: 0 }}>
        {durability}
      </Grid>
      <Grid item sm={12}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {t('reviews.features.availability')}
        </Typography>
      </Grid>
      <Grid item sm={5}>
        <Slider
          sx={{ '.MuiSlider-thumb': { boxShadow: 'none', width: 0, height: 0 }, '& .MuiSlider-track': { color: selectColor(availability) } }}
          aria-label="availability"
          aria-valuetext={availability.toString()}
          value={parseInt(availability, 10)}
          min={0}
          max={10}
          disabled
        />
      </Grid>
      <Grid item sm={1}>
        {availability}
      </Grid>
    </Grid>
  );
}

ProductShowFeatures.propTypes = {
  ean: proptypes.string.isRequired,
};

export default ProductShowFeatures;
